import { environment } from '../../../environments/environment';
import {
  PRISM_NEW_LEGACY_MAPS,
  PRISM_NEW_RENAMED_ENDPOINTS,
  PRISM_NEW_REQUEST_FIXES,
  PrismNewRequest,
  ProductPidLookup,
} from './prism-new-legacy-maps';
import { PRISM_NEW_NUMERIC_FIELDS, PRISM_NEW_STRING_FIELDS } from './prism-new-numeric-fields';
import { apiBaseUrlFor, endpointTable } from './prism-new-routing';

/**
 * prism-new answers `{ success, data, meta }` or `{ success: false, error: { code, message, details, issues } }`
 * and reshapes some payloads, while the CRM was written against the old API's bodies. A call routed to
 * prism-new gets its response turned back into the old body here, so nothing downstream changes.
 * Only prism-new responses are touched: with `prismNew.enabled` off this is all a no-op.
 */

const ORIGINAL_URL = 'prismNewOriginalUrl';

interface RequestLike {
  method?: string;
  url?: string;
  baseURL?: string;
  params?: any;
  data?: any;
  [ORIGINAL_URL]?: string;
}

const isPlainObject = (value: unknown): value is Record<string, any> =>
  !!value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype;

const findLegacyMap = endpointTable(PRISM_NEW_LEGACY_MAPS);
const findRename = endpointTable(PRISM_NEW_RENAMED_ENDPOINTS);
const findRequestFix = endpointTable(PRISM_NEW_REQUEST_FIXES);
const findNumericFields = endpointTable(PRISM_NEW_NUMERIC_FIELDS);
const findStringFields = endpointTable(PRISM_NEW_STRING_FIELDS);

const pathSegments = (url: string) => url.split(/[?#]/)[0].split('/').filter(Boolean);

/** A digit string (what a form input hands over) → number; anything else as it came. */
const toNumber = (value: unknown) => (typeof value === 'string' && /^\s*-?\d+(\.\d+)?\s*$/.test(value) ? Number(value) : value);
/** A number (what a number input or a numeric id hands over) → string; anything else as it came. */
const toText = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? String(value) : value);

/** `convert` at one swagger path (`a.b`, `a[]`, `a[].b`), copying every object/array it passes through. */
function convertAt(target: any, parts: string[], convert: (value: unknown) => unknown): any {
  if (!parts.length) return convert(target);
  if (target == null || typeof target !== 'object') return target;
  const [head, ...rest] = parts;
  const key = head.endsWith('[]') ? head.slice(0, -2) : head;
  if (!(key in target)) return target;
  const value = head.endsWith('[]')
    ? Array.isArray(target[key]) ? target[key].map((item: unknown) => convertAt(item, rest, convert)) : target[key]
    : convertAt(target[key], rest, convert);
  return { ...target, [key]: value };
}

export function isPrismNewCall(config?: RequestLike | null): boolean {
  return !!config && environment.prismNew.enabled && config.baseURL === environment.prismNew.apiUrl;
}

/**
 * Runs once per request routed to prism-new (a retry is left as prepared): remembers the CRM's path, applies renamed endpoints and
 * the swagger's request-side changes (numeric productPid, date formats, renamed filters), then turns
 * digit strings into numbers in the body fields the swagger types as numbers. The caller's params and
 * body objects are never mutated.
 */
export function preparePrismNewRequest(config: RequestLike, productPid: ProductPidLookup = () => undefined): void {
  /* Already prepared: a retry of the same request. */
  if (config[ORIGINAL_URL] !== undefined) return;
  const url = config.url ?? '';
  const method = config.method ?? 'get';
  config[ORIGINAL_URL] = url;
  const query = url.includes('?') ? url.slice(url.indexOf('?')) : '';
  const slash = url.startsWith('/') ? '/' : '';
  const renamed = findRename(method, url)?.(config.params ?? {});
  if (renamed) config.url = `${slash}${renamed}${query}`;

  const fix = findRequestFix(method, url);
  const numericFields = findNumericFields(method, url);
  const stringFields = findStringFields(method, url);
  if (!fix && !numericFields && !stringFields) return;
  const body = isPlainObject(config.data) ? { ...config.data } : undefined;
  const request: PrismNewRequest = { params: { ...(config.params ?? {}) }, segments: pathSegments(config.url ?? ''), body };
  fix?.(request, productPid);
  if (request.body) {
    request.body = (numericFields ?? []).reduce((b, field) => convertAt(b, field.split('.'), toNumber), request.body);
    request.body = (stringFields ?? []).reduce((b, field) => convertAt(b, field.split('.'), toText), request.body);
  }
  config.params = request.params;
  if (body) config.data = request.body;
  config.url = `${slash}${request.segments.join('/')}${query}`;
}

export function toLegacyErrorBody(body: any, status?: number): any {
  const error = body?.error;
  if (!error || typeof error !== 'object' || Array.isArray(error)) return body;
  const issues: string[] = Array.isArray(error.issues) ? error.issues.map((i: any) => i.message) : [];
  return {
    ...(error.details ?? {}), // `action` (REFRESH_TOKEN / RETRY_LOGIN) sat at the top level on the old API
    success: false,
    error: issues.length ? issues : error.message,
    code: error.code,
    requestId: body.meta?.requestId,
    timestamp: body.meta?.timestamp,
    statusCode: status,
  };
}

export function toLegacyBody(config: RequestLike, body: any, status?: number): any {
  if (!body || typeof body !== 'object' || !('success' in body)) return body;
  if (body.success === false) return toLegacyErrorBody(body, status);
  const url = config[ORIGINAL_URL] ?? config.url ?? '';
  const meta = body.meta ?? {};
  const map = findLegacyMap(config.method ?? 'get', url);
  const legacy = map
    ? map(body.data, { params: config.params ?? {}, segments: pathSegments(url), meta: meta.pagination })
    : { data: body.data };
  return { success: true, ...legacy, requestId: meta.requestId, timestamp: meta.timestamp, statusCode: status };
}

/** For main-API calls made outside ApiService (fetch / HttpClient): the old body, whichever API answered. */
export function legacyBodyFor(method: string, path: string, body: any): any {
  if (apiBaseUrlFor(method, path) !== environment.prismNew.apiUrl) return body;
  const query = path.includes('?') ? path.slice(path.indexOf('?') + 1) : '';
  const params = Object.fromEntries(new URLSearchParams(query));
  return toLegacyBody({ method, url: path, params, [ORIGINAL_URL]: path }, body);
}

/**
 * The old API streams `summary/query` as server-sent events. prism-new answers with one JSON body, or (with
 * `isStream`) with its own events: `data: {"type":"data","data":<text piece | object>}` … `data: {"type":"end"}`.
 * Both are replayed as the events the chat panel already parses. Old-API streams pass through untouched.
 */
export async function asQueryEventStream(response: Response): Promise<Response> {
  if (!(response.headers.get('content-type') ?? '').includes('application/json')) {
    if (!response.body || apiBaseUrlFor('POST', 'summary/query') !== environment.prismNew.apiUrl) return response;
    const body = response.body.pipeThrough(new TextDecoderStream()).pipeThrough(prismNewQueryEvents()).pipeThrough(new TextEncoderStream());
    return new Response(body, { status: response.status, headers: { 'content-type': 'text/event-stream' } });
  }
  const body = await response.json().catch(() => null);
  const answer = body?.data ?? {};
  const start = JSON.stringify({ sessionId: answer.sessionId, conversationId: answer.conversationId });
  const message = JSON.stringify({ message: answer.aiResponse ?? '', suggestedQuestions: answer.suggestedQuestions ?? [], conversationId: answer.conversationId ?? undefined });
  const events = `event: start\ndata: ${start}\n\ndata: ${message}\n\nevent: end\ndata: [DONE]\n`;
  return new Response(events, { status: response.status, headers: { 'content-type': 'text/event-stream' } });
}

/**
 * prism-new's `{"type": ...}` stream events → the old API's events. Each event goes out as one chunk with its
 * `event:` line, since the panel forgets an `event:` name at a chunk boundary.
 */
function prismNewQueryEvents(): TransformStream<string, string> {
  let buffer = '';
  let pending = ''; // `event:` lines waiting for their `data:` line
  const convert = (line: string): string => {
    if (!line.startsWith('data:')) return `${line}\n`;
    let event: any;
    try {
      event = JSON.parse(line.slice(5).trim());
    } catch {
      return `${line}\n`;
    }
    if (!event || typeof event !== 'object' || typeof event.type !== 'string') return `${line}\n`;
    const { type, data } = event;
    if (type === 'end') return 'event: end\ndata: [DONE]\n';
    // A text piece goes as {message} so newlines inside it survive the panel's line split.
    if (typeof data === 'string') return data ? `data: ${JSON.stringify({ message: data })}\n` : '';
    // Objects ({message}, {suggestions}, {conversationId}, ...) are what the old API sent as data.
    if (data && typeof data === 'object') return `data: ${JSON.stringify(data)}\n`;
    return '';
  };
  const push = (line: string, controller: TransformStreamDefaultController<string>) => {
    line = line.replace(/\r$/, '');
    if (!line) return;
    pending += convert(line);
    if (line.startsWith('data:')) {
      if (pending) controller.enqueue(`${pending}\n`);
      pending = '';
    }
  };
  return new TransformStream<string, string>({
    transform(chunk, controller) {
      buffer += chunk;
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) push(line, controller);
    },
    flush(controller) {
      if (buffer) push(buffer, controller);
      if (pending) controller.enqueue(`${pending}\n`);
    },
  });
}
